import React from 'react';
import Button from './Button';
import Badge from './Badge';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 hero-gradient opacity-95" />
      <div className="absolute inset-0 bg-black/30" />

      {/* Soft background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-slate-800/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-slate-700/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[32rem] bg-slate-600/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
      </div>

      <div className="relative container-xl py-20 xl:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16 items-center">
          {/* Left: copy + CTAs */}
          <div className="text-center lg:text-left space-y-8">
            {/* Trust badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              <Badge variant="outline" className="bg-white/10 text-white border-white/30">
                For SMEs & schools
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30">
                End-to-end ICT support
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30">
                Based in the Philippines
              </Badge>
            </div>

            {/* Main heading */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl logo-font text-white leading-tight tracking-tight">
                Smart ICT solutions for growing Philippine businesses.
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 font-medium leading-relaxed max-w-2xl mx-auto lg:mx-0">
                We design and deploy secure systems, dashboards, and web platforms for businesses,
                schools, and SMEs—built to be reliable, scalable, and easy to maintain.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start">
              <Button
                variant="primary"
                size="lg"
                onClick={() => scrollToSection('#contact')}
                className="px-8 py-4 text-base sm:text-lg"
              >
                Talk to an ICT expert
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => scrollToSection('#solutions')}
                className="px-8 py-4 text-base sm:text-lg"
              >
                View solutions
              </Button>
            </div>

            {/* Supporting line */}
            <p className="text-sm sm:text-base text-white/70 max-w-xl mx-auto lg:mx-0">
              From first assessment to ongoing support, we partner with you to modernize
              infrastructure, streamline operations, and keep your systems running smoothly.
            </p>
          </div>

          {/* Right: metrics panel */}
          <div className="flex justify-center lg:justify-end">
            <div className="nc-card-soft w-full max-w-md p-6 sm:p-8 text-left bg-white/5 border-white/10">
              <div className="mb-6">
                <p className="text-xs font-semibold tracking-wide text-white/70 uppercase mb-2">
                  Snapshot
                </p>
                <h2 className="text-xl font-semibold text-white">
                  Operational reliability, by design.
                </h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white/70">
                    Typical uptime for deployed systems
                  </p>
                  <p className="text-2xl font-semibold text-white">99.9%</p>
                </div>
                <div className="border-t border-white/10" />
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white/70">
                    Average response time on support requests
                  </p>
                  <p className="text-2xl font-semibold text-white">&lt; 2 hrs</p>
                </div>
                <div className="border-t border-white/10" />
                <div className="flex items-center justify-between">
                  <p className="text-sm text-white/70">
                    Projects delivered across PH organizations
                  </p>
                  <p className="text-2xl font-semibold text-white">25+</p>
                </div>
              </div>

              <div className="mt-6 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-xs sm:text-sm text-white/75">
                Tailored for SMEs, private schools, and growing teams that need enterprise-grade
                ICT without the enterprise complexity.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;



